/* Original-file storage (R2 behind the worker). Uploads are best-effort:
   when the bucket isn't bound the worker answers 501 and callers simply
   don't record a fileId — previews still work, downloads stay hidden.
   Files are session-gated server-side; the token rides as a query param so
   plain <a href> downloads and <image href> previews keep working. */
import { useEffect, useState } from "react";
import { uid } from "./store";
import { getManagerToken, authHeader } from "./managerAuth";

/* ---- global upload progress bus -------------------------------------------
   Every upload that goes through this module reports itself here, so ONE
   app-wide bar can show real byte progress for whatever is in flight — a
   115-sheet drawing set, a 40 MB contract, a site video. Before this, a big
   upload sat behind a static "Saving…" with no way to tell moving from stuck. */
export interface UploadItem { key: string; name: string; pct: number; bytes: number; doneAt?: number }
const uploads = new Map<string, UploadItem>();
const upSubs = new Set<() => void>();
const emitUploads = () => upSubs.forEach((fn) => fn());

/* An in-flight upload dies with its tab. Navigating INSIDE the app is safe —
   the XHR lives in this module, not in any page — but closing or reloading
   the browser tab kills it, so while anything is uploading the browser asks
   before letting the tab go. */
function beforeUnloadGuard(e: BeforeUnloadEvent) {
  if ([...uploads.values()].some((u) => !u.doneAt)) {
    e.preventDefault();
    e.returnValue = "";   // required by the spec for the prompt to appear
  }
}

function trackStart(key: string, name: string, bytes: number) {
  if (![...uploads.values()].some((u) => !u.doneAt)) {
    window.addEventListener("beforeunload", beforeUnloadGuard);
  }
  uploads.set(key, { key, name, pct: 0, bytes });
  emitUploads();
}
function trackPct(key: string, pct: number) {
  const u = uploads.get(key);
  if (u && pct - u.pct >= 2) { u.pct = pct; emitUploads(); }
}
function trackEnd(key: string, ok: boolean) {
  const u = uploads.get(key);
  if (!u) return;
  // drop the leave-guard once nothing else is still moving
  if (![...uploads.values()].some((x) => x.key !== key && !x.doneAt)) {
    window.removeEventListener("beforeunload", beforeUnloadGuard);
  }
  if (ok) {
    u.pct = 100;
    u.doneAt = Date.now();
    emitUploads();
    setTimeout(() => { uploads.delete(key); emitUploads(); }, 3500);
  } else {
    uploads.delete(key);
    emitUploads();
  }
}

/** Live view of every upload in flight — the app-wide bar renders from this. */
export function useUploads(): UploadItem[] {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    upSubs.add(fn);
    return () => { upSubs.delete(fn); };
  }, []);
  return [...uploads.values()];
}

/** On a public share page there is no session — the package token is what
    lets the viewer fetch the sheets' renders. Set once by the page. */
let shareToken: string | null = null;
export function setShareToken(t: string | null) { shareToken = t; }
export function fileUrl(id: string, name?: string, inline = false): string {
  const q = new URLSearchParams();
  if (name) q.set("name", name);
  if (inline) q.set("inline", "1");
  const t = getManagerToken();
  if (t) q.set("t", t);
  if (shareToken) q.set("pk", shareToken);
  const qs = q.toString();
  return `/api/file/${id}${qs ? `?${qs}` : ""}`;
}

/** Store a file; returns its id, or null when storage is off / upload failed. */
/** Upload with byte-level progress. fetch cannot report upload progress, so
    this one path uses XMLHttpRequest — the progress bar the site crew watches
    is real bytes on the wire, not an animation. Resolves null on any failure
    (timeout included) exactly like uploadFile. */
export function uploadFileProgress(
  file: Blob, name: string, timeoutMs: number,
  onPct?: (pct: number) => void,
): Promise<string | null> {
  if (file.size > MPU_THRESHOLD) return uploadFileMultipart(file, name, onPct);
  const id = uid("f-");
  lastError = "";
  const total = file.size || 1;
  const run = putWithRetry(fileUrl(id, name), file, file.type || "application/octet-stream", (loaded) => onPct?.(Math.round((loaded / total) * 100)))
    .then((res) => {
      if (!res || res.status < 200 || res.status >= 300) return null;
      try { return (JSON.parse(res.text || "{}") as { ok?: boolean }).ok ? id : null; } catch { return null; }
    });
  // a caller that must not block (report filing) still gets its hard cap
  if (timeoutMs > 0) return Promise.race([run, new Promise<null>((r) => setTimeout(() => { lastError = "the upload took too long"; r(null); }, timeoutMs))]);
  return run;
}

/* Past this size a single PUT cannot fit through the platform's 100MB request
   limit, so the file goes up in slices through the multipart endpoints. Kept
   safely under the simple path's 95MB cap. */
const MPU_THRESHOLD = 80 * 1024 * 1024;
const MPU_PART_SIZE = 40 * 1024 * 1024;

/* ---- one PUT with an idle deadline and retries ------------------------------
   A site connection that drops mid-upload never errors — the request just
   sits. A whole-request timeout is wrong too (a 3 MB photo on 3G legitimately
   takes minutes). So the deadline is IDLE time: no bytes moving for a minute
   ends the attempt, and a failed attempt is retried twice with a pause. */
const IDLE_MS = 60_000;
/** Why the last upload failed, in words — "too large", "no connection"… */
let lastError = "";
export function lastUploadError(): string { return lastError; }
function xhrPut(url: string, body: Blob, type: string, onBytes?: (loaded: number) => void): Promise<{ status: number; text: string } | null> {
  return new Promise((resolve) => {
    let idle: ReturnType<typeof setTimeout> | null = null;
    try {
      const xhr = new XMLHttpRequest();
      const kick = () => { if (idle) clearTimeout(idle); idle = setTimeout(() => { try { xhr.abort(); } catch { /* gone */ } resolve(null); }, IDLE_MS); };
      xhr.open("PUT", url);
      const h = authHeader() as Record<string, string>;
      for (const k of Object.keys(h)) xhr.setRequestHeader(k, h[k]);
      xhr.setRequestHeader("Content-Type", type || "application/octet-stream");
      xhr.upload.onprogress = (e) => { kick(); if (e.lengthComputable) onBytes?.(e.loaded); };
      xhr.onload = () => { if (idle) clearTimeout(idle); resolve({ status: xhr.status, text: xhr.responseText || "" }); };
      xhr.onerror = () => { if (idle) clearTimeout(idle); resolve(null); };
      xhr.onabort = () => { if (idle) clearTimeout(idle); resolve(null); };
      kick();
      xhr.send(body);
    } catch { if (idle) clearTimeout(idle); resolve(null); }
  });
}
async function putWithRetry(url: string, body: Blob, type: string, onBytes?: (loaded: number) => void): Promise<{ status: number; text: string } | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    const res = await xhrPut(url, body, type, onBytes);
    if (res && res.status >= 200 && res.status < 300) return res;
    // a refusal the server will repeat (too large, bad request) is not retried
    if (res && res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
      try { lastError = (JSON.parse(res.text) as { error?: string }).error || `refused (${res.status})`; } catch { lastError = `refused (${res.status})`; }
      if (res.status === 401) lastError = "you are signed out — sign in again";
      return res;
    }
    lastError = res ? `server error (${res.status})` : "the connection dropped";
    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
  }
  return null;
}

function putPart(url: string, chunk: Blob, onBytes: (loaded: number) => void): Promise<{ etag: string } | null> {
  return putWithRetry(url, chunk, "application/octet-stream", onBytes).then((res) => {
    if (!res || res.status < 200 || res.status >= 300) return null;
    try {
      const out = JSON.parse(res.text || "{}") as { ok?: boolean; etag?: string };
      return out?.ok && out.etag ? { etag: out.etag } : null;
    } catch { return null; }
  });
}

async function uploadFileMultipart(file: Blob, name: string, onPct?: (pct: number) => void): Promise<string | null> {
  try {
    const init = await fetch("/api/file-mpu", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ name, type: file.type || "application/octet-stream", size: file.size }),
    });
    if (!init.ok) return null;
    const meta = await init.json().catch(() => null) as { ok?: boolean; id?: string; uploadId?: string } | null;
    if (!meta?.ok || !meta.id || !meta.uploadId) return null;
    const { id, uploadId } = meta;

    const parts: { partNumber: number; etag: string }[] = [];
    const total = file.size;
    let doneBytes = 0;
    for (let off = 0, n = 1; off < total; off += MPU_PART_SIZE, n++) {
      const chunk = file.slice(off, Math.min(off + MPU_PART_SIZE, total));
      const url = `/api/file-mpu/${id}/${n}?u=${encodeURIComponent(uploadId)}`;
      const report = (loaded: number) => onPct?.(Math.min(99, Math.round(((doneBytes + loaded) / total) * 100)));
      // one retry per part — site connections drop mid-set often enough to earn it
      let res = await putPart(url, chunk, report);
      if (!res) res = await putPart(url, chunk, report);
      if (!res) {
        void fetch(`/api/file-mpu/${id}?u=${encodeURIComponent(uploadId)}`, { method: "DELETE", headers: authHeader() }).catch(() => undefined);
        return null;
      }
      parts.push({ partNumber: n, etag: res.etag });
      doneBytes += chunk.size;
    }
    const fin = await fetch(`/api/file-mpu/${id}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ uploadId, parts }),
    });
    if (!fin.ok) return null;
    const out = await fin.json().catch(() => null) as { ok?: boolean } | null;
    if (out?.ok) { onPct?.(100); return id; }
    return null;
  } catch {
    return null;
  }
}

export async function uploadFile(file: File | Blob, name: string, timeoutMs = 0): Promise<string | null> {
  /* Sizeable files go over XHR so their progress is visible app-wide; tiny
     ones aren't worth a bar. Same contract either way: id on success, null on
     any failure. */
  if (file.size > 300_000) {
    const key = uid("up-");
    trackStart(key, name, file.size);
    const rid = await uploadFileProgress(file, name, timeoutMs, (pct) => trackPct(key, pct));
    trackEnd(key, !!rid);
    return rid;
  }
  // small files take the same path: idle deadline, retries, and a reason on failure
  return uploadFileProgress(file, name, timeoutMs);
}

